/**
 * Helpers for optimistic drag-and-drop on the Kanban board (lists + cards).
 */

export const getBoardListsDroppableId = (boardId) => `board-lists-${boardId}`;

export function applyListDrag(lists, { source, destination, draggableId }) {
  if (!destination) return null;
  if (source.index === destination.index) return null;

  const sorted = [...lists].sort((a, b) => a.position - b.position);
  const [moved] = sorted.splice(source.index, 1);
  if (!moved || String(moved.listId) !== String(draggableId)) return null;

  sorted.splice(destination.index, 0, moved);
  return sorted.map((list, index) => ({ ...list, position: index }));
}

export function buildListMovePayload(lists) {
  return {
    affectedLists: lists.map((list) => ({
      listId: list.listId,
      position: list.position,
    })),
  };
}

export function applyCardDrag(cards, { source, destination, draggableId }) {
  if (!destination) return null;
  if (
    source.droppableId === destination.droppableId &&
    source.index === destination.index
  ) {
    return null;
  }

  const byList = {};
  cards.forEach((card) => {
    if (!byList[card.listId]) byList[card.listId] = [];
    byList[card.listId].push({ ...card });
  });

  Object.keys(byList).forEach((listId) => {
    byList[listId].sort((a, b) => a.position - b.position);
  });

  const sourceListId = source.droppableId;
  const destListId = destination.droppableId;
  const sourceCards = byList[sourceListId] || [];
  const [moved] = sourceCards.splice(source.index, 1);
  if (!moved || moved.cardId !== draggableId) return null;

  moved.listId = destListId;
  if (!byList[destListId]) byList[destListId] = [];
  byList[destListId].splice(destination.index, 0, moved);

  const affectedListIds =
    sourceListId === destListId ? [sourceListId] : [sourceListId, destListId];

  const updatedCards = [];
  affectedListIds.forEach((listId) => {
    (byList[listId] || []).forEach((card, index) => {
      updatedCards.push({ ...card, listId, position: index });
    });
  });

  const updatedIds = new Set(updatedCards.map((c) => c.cardId));
  const unchanged = cards
    .filter((c) => !updatedIds.has(c.cardId))
    .map((c) => ({ ...c }));

  return [...unchanged, ...updatedCards];
}

export function buildMovePayload(cards, { source, destination, draggableId }) {
  const affectedListIds =
    source.droppableId === destination.droppableId
      ? [source.droppableId]
      : [source.droppableId, destination.droppableId];

  const affectedCards = cards
    .filter((c) => affectedListIds.includes(c.listId))
    .sort((a, b) => {
      if (a.listId !== b.listId) return a.listId.localeCompare(b.listId);
      return a.position - b.position;
    })
    .map((c) => ({
      cardId: c.cardId,
      listId: c.listId,
      position: c.position,
    }));

  return {
    listId: destination.droppableId,
    position: destination.index,
    affectedCards,
  };
}
